import { supabase } from '../lib/supabase';
import { productionSMSService } from './productionSMSService';


/**
 * SMS Alert Service for urgent compliance violations and treatment alerts
 * Sends immediate SMS notifications to field teams via Twilio
 * Now delegates to productionSMSService for Twilio-compliant delivery
 */
export const smsAlertService = {
  /**
   * Send SMS alert to field team members
   */
  async sendFieldTeamAlert(alertData) {
    try {
      const { type, severity, title, message, recipientUserIds, metadata } = alertData;

      // Delegate to production SMS service (handles priority filter, rate limit, dedup, consent)
      const batchResult = await productionSMSService?.sendBatchAlerts(recipientUserIds, {
        alertType: type,
        severity,
        title,
        message,
        resourceId: metadata?.alertId || metadata?.planId || '',
        metadata
      });

      return {
        totalRecipients: batchResult?.totalRecipients,
        sent: batchResult?.sent,
        failed: batchResult?.failed,
        results: batchResult?.results
      };
    } catch (error) {
      console.error('Error sending field team alert:', error);
      throw error;
    }
  },

  /**
   * Send compliance violation SMS alert
   */
  async sendComplianceViolationAlert(violationData) {
    try {
      const { alertId, severity, title, message, companyId } = violationData;

      const { data: employees, error: employeesError } = await supabase
        ?.from('company_employees')
        ?.select('user_id, position')
        ?.eq('company_id', companyId)
        ?.eq('status', 'active')
        ?.in('position', ['Field Manager', 'Compliance Officer', 'Operations Manager', 'Farm Manager']);

      if (employeesError) throw employeesError;
      if (!employees || employees?.length === 0) {
        return { sent: false, reason: 'No field team members found' };
      }

      const recipientUserIds = employees?.map(e => e?.user_id);

      return await this.sendFieldTeamAlert({
        type: 'compliance_violation',
        severity,
        title: title || 'Compliance Violation Alert',
        message,
        recipientUserIds,
        metadata: { alertId, companyId }
      });
    } catch (error) {
      console.error('Error sending compliance violation alert:', error);
      throw error;
    }
  },

  /**
   * Send treatment plan alert
   */
  async sendTreatmentAlert(treatmentData) {
    try {
      const { planId, severity, title, message, companyId, assignedUserId } = treatmentData;

      const { data: employees, error: employeesError } = await supabase
        ?.from('company_employees')
        ?.select('user_id, position')
        ?.eq('company_id', companyId)
        ?.eq('status', 'active')
        ?.in('position', ['Field Manager', 'IPM Specialist', 'Operations Manager', 'Farm Manager']);

      if (employeesError) throw employeesError;

      let recipientUserIds = employees?.map(e => e?.user_id) || [];
      if (assignedUserId && !recipientUserIds?.includes(assignedUserId)) {
        recipientUserIds?.push(assignedUserId);
      }

      if (recipientUserIds?.length === 0) {
        return { sent: false, reason: 'No field team members found' };
      }

      return await this.sendFieldTeamAlert({
        type: 'treatment_alert',
        severity,
        title: title || 'Treatment Plan Alert',
        message,
        recipientUserIds,
        metadata: { planId, companyId }
      });
    } catch (error) {
      console.error('Error sending treatment alert:', error);
      throw error;
    }
  },

  /**
   * Log SMS delivery for audit trail (legacy - now handled by productionSMSService)
   */
  async logSMSDelivery(deliveryData) {
    return productionSMSService?.logSMS({
      userId: deliveryData?.userId,
      phoneNumber: deliveryData?.phoneNumber,
      message: deliveryData?.message,
      messageSid: deliveryData?.messageSid,
      alertType: deliveryData?.alertType,
      severity: deliveryData?.severity,
      status: deliveryData?.status || 'sent'
    });
  },

  /**
   * Get SMS delivery statistics
   */
  async getSMSStats(companyId, dateRange = {}) {
    return productionSMSService?.getSMSStats(null, dateRange);
  }
};

export default smsAlertService;